// V11.19 — Aperçu embarqué des derniers posts Instagram / TikTok
//
// Composant réutilisable pour afficher un post social directement dans une
// sidebar (fiche Athlète). Il charge le script officiel oEmbed du réseau
// (une seule fois par session) et rend le <blockquote> attendu.
//
// Volontairement autonome : aucune dépendance à une API tierce payante, aucun
// scraping. L'admin/coach fournit l'URL exacte du post via l'éditeur de fiche.
//
// Formats supportés :
//   • Instagram : https://www.instagram.com/p/{code}/  ou  /reel/{code}/  ou  /tv/{code}/
//   • TikTok    : https://www.tiktok.com/@{user}/video/{id}
//
// Design tokens : bordure `border`, arrondi `rounded-xl`, fond `bg-card`,
// hauteur mini fixe pour éviter le layout shift au chargement du script tiers.

import { useEffect, useRef, useState } from "react";
import { Instagram, ExternalLink, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

// ---------------------------------------------------------------------------
// Icône TikTok inline (lucide n'en fournit pas d'officielle)
// ---------------------------------------------------------------------------
function TiktokGlyph({ className = "h-4 w-4" }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.66a8.16 8.16 0 0 0 4.77 1.52V6.69a4.85 4.85 0 0 1-1.84 0Z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Détection & normalisation des URLs
// ---------------------------------------------------------------------------
export function detectSocialPlatform(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const url = rawUrl.trim();
  if (/instagram\.com\/(p|reel|tv)\//i.test(url)) return "instagram";
  if (/tiktok\.com\/@[^/]+\/video\/\d+/i.test(url) || /vm\.tiktok\.com\//i.test(url)) return "tiktok";
  return null;
}

// ---------------------------------------------------------------------------
// Chargement paresseux d'un script tiers, une seule fois par session
// ---------------------------------------------------------------------------
const SCRIPT_STATUS = {}; // { src: "loading" | "ready" | "error" }
const SCRIPT_WAITERS = {}; // { src: Array<fn> }

function loadEmbedScript(src) {
  return new Promise((resolve, reject) => {
    if (SCRIPT_STATUS[src] === "ready") return resolve();
    if (SCRIPT_STATUS[src] === "error") return reject(new Error("script failed"));
    if (!SCRIPT_WAITERS[src]) SCRIPT_WAITERS[src] = [];
    SCRIPT_WAITERS[src].push({ resolve, reject });
    if (SCRIPT_STATUS[src] === "loading") return;
    SCRIPT_STATUS[src] = "loading";

    const el = document.createElement("script");
    el.async = true;
    el.src = src;
    el.onload = () => {
      SCRIPT_STATUS[src] = "ready";
      (SCRIPT_WAITERS[src] || []).forEach((w) => w.resolve());
      SCRIPT_WAITERS[src] = [];
    };
    el.onerror = () => {
      SCRIPT_STATUS[src] = "error";
      (SCRIPT_WAITERS[src] || []).forEach((w) => w.reject(new Error("script failed")));
      SCRIPT_WAITERS[src] = [];
    };
    document.body.appendChild(el);
  });
}

// ---------------------------------------------------------------------------
// Composant principal
// ---------------------------------------------------------------------------
export function SocialPostEmbed({ url, testId }) {
  const platform = detectSocialPlatform(url);
  const containerRef = useRef(null);
  const [status, setStatus] = useState("loading"); // "loading" | "ready" | "error"

  useEffect(() => {
    if (!platform) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    setStatus("loading");

    const script =
      platform === "instagram"
        ? "https://www.instagram.com/embed.js"
        : "https://www.tiktok.com/embed.js";

    loadEmbedScript(script)
      .then(() => {
        if (cancelled) return;
        try {
          // Force le re-parse du DOM par le script tiers (Instagram: process(); TikTok: re-instancie)
          if (platform === "instagram" && window.instgrm?.Embeds?.process) {
            window.instgrm.Embeds.process();
          }
          // TikTok se re-parse automatiquement à chaque insertion.
          setStatus("ready");
        } catch (e) {
          setStatus("error");
        }
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [platform, url]);

  if (!platform) {
    return (
      <div
        className="rounded-lg border border-dashed border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive flex items-start gap-2"
        data-testid={testId ? `${testId}-invalid` : undefined}
      >
        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          URL non reconnue. Utilise une URL de post Instagram (<code>/p/</code>, <code>/reel/</code>) ou TikTok (<code>/video/</code>).
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl border bg-card overflow-hidden"
      data-testid={testId || `social-embed-${platform}`}
    >
      {/* En-tête compact avec logo & lien direct */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
        {platform === "instagram" ? (
          <Instagram className="h-4 w-4 text-pink-600" />
        ) : (
          <TiktokGlyph className="h-4 w-4 text-foreground" />
        )}
        <span className="text-xs font-semibold uppercase tracking-wide">
          {platform === "instagram" ? "Instagram" : "TikTok"}
        </span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
          data-testid={`${testId || "social-embed"}-open`}
          title="Ouvrir le post"
        >
          Ouvrir <ExternalLink className="h-3 w-3" />
        </a>
      </div>

      {/* Corps embed */}
      <div ref={containerRef} className="p-2 min-h-[240px] flex items-center justify-center">
        {status === "loading" && (
          <div className="w-full space-y-2 animate-pulse py-6">
            <div className="h-3 w-24 bg-muted rounded" />
            <div className="h-40 w-full bg-muted rounded" />
            <div className="h-3 w-32 bg-muted rounded" />
          </div>
        )}

        {status === "error" && (
          <div className="text-xs text-muted-foreground text-center px-3 py-4">
            Impossible de charger l'aperçu.{" "}
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="underline underline-offset-2"
            >
              Voir sur {platform === "instagram" ? "Instagram" : "TikTok"}
            </a>
            .
          </div>
        )}

        {/* Blockquotes rendus en permanence pour laisser les scripts oEmbed les remplacer */}
        <div
          className="w-full"
          style={{ display: status === "ready" ? "block" : "none" }}
        >
          {platform === "instagram" && (
            <blockquote
              className="instagram-media"
              data-instgrm-permalink={url}
              data-instgrm-version="14"
              style={{
                background: "transparent",
                border: 0,
                margin: "0 auto",
                maxWidth: 540,
                width: "100%",
              }}
            >
              <a href={url} target="_blank" rel="noreferrer noopener">
                Voir ce post sur Instagram
              </a>
            </blockquote>
          )}

          {platform === "tiktok" && (
            <TikTokBlockquote url={url} />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blockquote TikTok — extrait video_id de l'URL pour l'attribut data-video-id
// ---------------------------------------------------------------------------
function TikTokBlockquote({ url }) {
  const m = url.match(/\/video\/(\d+)/);
  const videoId = m ? m[1] : "";
  return (
    <blockquote
      className="tiktok-embed"
      cite={url}
      data-video-id={videoId}
      style={{ maxWidth: 605, minWidth: 240, margin: "0 auto" }}
    >
      <section>
        <a href={url} target="_blank" rel="noreferrer noopener">
          Voir cette vidéo sur TikTok
        </a>
      </section>
    </blockquote>
  );
}

export default SocialPostEmbed;
